import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Image, Platform, Pressable, View, useWindowDimensions } from 'react-native';
import { Asset } from 'expo-asset';
import { useAppDispatch } from '../../hooks/store-hook';
import { lightBoxActions } from '../../store/lightBoxSlice';
import useTranslation from '../../hooks/translation-hook';
import CustomCheckbox from '../common/CustomCheckbox';
import Label from '../common/Label';

export interface AirportTripSoonPopupProps {
  onClose?(dontShowAgain: boolean): void;
  onProceed?(dontShowAgain: boolean): void;
}

const planeBannerMobile = require('../../../assets/plane-banner-mobile.png');
const planeBannerMobileAspectRatio = 148 / 157;
void Asset.fromModule(planeBannerMobile)
  .downloadAsync()
  .catch(() => undefined);

const useNativeAnimationDriver = Platform.OS !== 'web';

const AirportTripSoonPopup = (props: AirportTripSoonPopupProps) => {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const layout = useWindowDimensions();
  const [checked, setChecked] = useState<boolean>(false);
  const checkedRef = useRef(false);
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(-12)).current;
  const closingRef = useRef(false);

  useEffect(() => {
    checkedRef.current = checked;
  }, [checked]);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: 220,
        easing: Easing.out(Easing.quad),
        useNativeDriver: useNativeAnimationDriver,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: 220,
        easing: Easing.out(Easing.quad),
        useNativeDriver: useNativeAnimationDriver,
      }),
    ]).start();
  }, [opacity, translateY]);

  const finishDismiss = (proceed: boolean) => {
    if (closingRef.current) {
      return;
    }
    closingRef.current = true;
    const dontShowAgain = checkedRef.current;
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 0,
        duration: 180,
        easing: Easing.in(Easing.quad),
        useNativeDriver: useNativeAnimationDriver,
      }),
      Animated.timing(translateY, {
        toValue: -8,
        duration: 180,
        easing: Easing.in(Easing.quad),
        useNativeDriver: useNativeAnimationDriver,
      }),
    ]).start(() => {
      if (proceed) {
        props.onProceed?.(dontShowAgain);
      } else {
        props.onClose?.(dontShowAgain);
      }
      setChecked(false);
      dispatch(lightBoxActions.setVisibility(false));
      dispatch(lightBoxActions.setOnClose(undefined));
      closingRef.current = false;
    });
  };

  useEffect(() => {
    dispatch(
      lightBoxActions.setOnClose(() => {
        dispatch(lightBoxActions.setOnClose(undefined));
        finishDismiss(false);
      }),
    );
  }, [dispatch, props.onClose, props.onProceed]);

  const handleClose = () => {
    finishDismiss(false);
  };

  const handleProceed = () => {
    finishDismiss(true);
  };

  const isWeb = Platform.OS === 'web';
  const isCompactAndroidLayout = Platform.OS === 'android' && layout.width <= 380;
  const goButtonMarginTop = Platform.OS === 'ios' ? -6 : -3;
  const contentLeftPadding = isWeb ? 106 : 100;
  const popupTitleFontSize = isCompactAndroidLayout ? 16 : 19;
  const popupTitleLineHeight = isCompactAndroidLayout ? 18 : 21;

  return (
    <Animated.View
      style={{
        width: layout.width > 1200 ? 520 : Math.min(layout.width - 16, 366),
        backgroundColor: '#15a6eb',
        borderRadius: 10,
        overflow: 'hidden',
        paddingTop: 6,
        paddingRight: 6,
        paddingBottom: 6,
        paddingLeft: 0,
        opacity,
        transform: [{ translateY }],
      }}
    >
      <Pressable onPress={handleClose} style={{ position: 'absolute', top: 2, right: 10, zIndex: 3 }}>
        <Label style={{ color: '#ffffff', fontSize: 34, lineHeight: 34 }}>×</Label>
      </Pressable>

      <Image
        source={planeBannerMobile}
        style={
          {
            position: 'absolute',
            left: 0,
            zIndex: 2,
            ...(isWeb
              ? {
                  top: 0,
                  height: '100%',
                  aspectRatio: planeBannerMobileAspectRatio,
                }
              : {
                  top: -32,
                  bottom: 0,
                  width: 94,
                }),
          } as any
        }
        resizeMode="contain"
      />

      <View style={{ borderRadius: 8, overflow: 'hidden' }}>
        <View
          style={{
            minHeight: 64,
            justifyContent: 'center',
            paddingLeft: contentLeftPadding,
            paddingRight: 106,
            paddingTop: 0,
            paddingBottom: 0,
          }}
        >
          <Pressable onPress={handleProceed}>
            <Label style={{ color: '#ffffff', fontWeight: '700', fontSize: popupTitleFontSize, lineHeight: popupTitleLineHeight }} numberOfLines={1}>
              {t('airport-trip-popup-title')}
            </Label>
            <Label style={{ color: '#ffffff', fontWeight: '700', fontSize: 15, marginTop: 1, lineHeight: 17 }} numberOfLines={1}>
              {t('airport-trip-popup-subtitle')}
            </Label>
          </Pressable>

          <Pressable
            onPress={handleProceed}
            style={{
              position: 'absolute',
              right: 18,
              top: '50%',
              marginTop: goButtonMarginTop,
              minWidth: 72,
              height: 37,
              borderRadius: 18.5,
              backgroundColor: '#032b45',
              alignItems: 'center',
              justifyContent: 'center',
              paddingHorizontal: 14,
            }}
          >
            <Label style={{ color: '#ffffff', fontSize: 13, fontWeight: '700' }}>{t('airport-trip-popup-go')}</Label>
          </Pressable>
        </View>

        <View
          style={{
            paddingLeft: contentLeftPadding,
            paddingRight: 12,
            paddingBottom: 2,
            marginTop: -2,
          }}
        >
          <CustomCheckbox
            value={'dontShowAirportTripSoon'}
            checked={checked}
            label={t('airport-trip-popup-dont-show')}
            markerColor={'#000000'}
            style={{
              width: '100%',
              height: 'auto',
              minHeight: 14,
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 6,
            }}
            textStyle={{
              color: '#053e63',
              fontSize: 11,
              lineHeight: 12,
              flexGrow: 1,
              flexShrink: 1,
            }}
            boxStyle={{ borderColor: '#74d0c8', borderWidth: 1.5, backgroundColor: '#f2fcff', width: 14, height: 14, marginTop: 1 }}
            onChange={setChecked}
          />
        </View>
      </View>
    </Animated.View>
  );
};

export default AirportTripSoonPopup;
